import { CommonModule } from "@angular/common";
import { Component, computed, effect, inject, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { Router } from "@angular/router";
import { injectLocalStorage } from "ngxtension/inject-local-storage";
import { firstValueFrom } from "rxjs";
import { environment } from "src/environments/environment";
import { BusyCoverDirective } from "src/app/core/ui/busy-cover/busy-cover.directive";
import { StripePlan } from "../../interface";
import { AuthService } from "../../services/auth/auth.service";
import { SubscriptionService } from "../../services/subscription/subscription.service";

interface PricingOption {
  id: string;
  price: number;
  currency: string;
  interval: string;
  plan: StripePlan;
}

interface PricingTier {
  name: string;
  monthly?: PricingOption;
  annual?: PricingOption;
  subscribed: boolean;
  comingSoon?: boolean;
}

const TIER_NAMES = ["Starter", "Pro", "Max"];

@Component({
  selector: "app-plans",
  standalone: true,
  imports: [CommonModule, BusyCoverDirective],
  templateUrl: "./plans.component.html",
  styleUrl: "./plans.component.scss",
})
export class PlansComponent {
  private subscriptionService = inject(SubscriptionService);
  private auth = inject(AuthService);
  private router = inject(Router);

  // Pre-selected plan from website (cleared on checkout redirect)
  private selectedPlan = injectLocalStorage<string | null>("selectedPlan", { defaultValue: null });

  pricingUrl = `${environment.WEBSITE}/pricing`;

  // State signals
  private intervalInitialized = !!this.selectedPlan();
  isAnnual = signal(this.selectedPlan()?.includes("annual") ?? false);
  busy = signal(false);
  error = signal<string | undefined>(undefined);
  autoProcessing = signal(!!this.selectedPlan());

  // Data signals
  private user = toSignal(this.auth.$user);
  private plans = toSignal(this.subscriptionService.getPlans());

  // Computed: pricing tiers with Stripe data and subscription status
  pricingTiers = computed(() => {
    const plans = this.plans();
    const user = this.user();

    return TIER_NAMES.map((name) => {
      const tier: PricingTier = { name, subscribed: false };

      // Attach Stripe pricing options
      (plans || []).forEach((plan: StripePlan) => {
        const tierName = plan.name.replace("ClassJax ", "").toLowerCase();
        if (tierName !== name.toLowerCase()) return;

        const option: PricingOption = {
          id: plan.id,
          price: plan.price.value / 100,
          currency: plan.price.currency,
          interval: plan.recurring.interval,
          plan,
        };

        if (plan.lookup_key.includes("annual")) {
          tier.annual = option;
        } else {
          tier.monthly = option;
        }
      });

      // Check subscription status
      if (user?.subscribed) {
        tier.subscribed = user.subscribed.startsWith(name.toLowerCase());
      }

      return tier;
    });
  });

  hasPaidPlans = computed(() =>
    this.pricingTiers().some((t: PricingTier) => t.name !== "Starter")
  );

  // Check if user is eligible for a free trial
  isTrialEligible = computed(() => {
    const user = this.user();
    return user?.is_trial_eligible ?? true;
  });

  constructor() {
    // Set initial interval from user's subscription (if no pre-selected plan)
    effect(() => {
      const user = this.user();
      if (this.intervalInitialized || !user?.subscribed) return;
      this.intervalInitialized = true;
      this.isAnnual.set(user.subscribed.includes("annual"));
    }, { allowSignalWrites: true });

    // Auto-subscribe effect when plans load and there's a pre-selected plan
    effect(() => {
      const tiers = this.pricingTiers();
      const plan = this.selectedPlan();
      if (!plan || tiers.length === 0) return;

      const tierName = plan.replace(/_annual|_monthly/g, "");
      const tier = tiers.find(
        (t: PricingTier) => t.name.toLowerCase() === tierName.toLowerCase()
      );

      if (tier && !tier.subscribed) {
        this.subscribeTo(tier);
      }
    }, { allowSignalWrites: true });
  }

  toggleInterval() {
    this.isAnnual.update((v) => !v);
  }

  getPrice(tier: PricingTier): number {
    return this.isAnnual()
      ? (tier.annual?.price || 0)
      : (tier.monthly?.price || 0);
  }

  getCurrency(tier: PricingTier): string {
    return this.isAnnual()
      ? (tier.annual?.currency || "usd")
      : (tier.monthly?.currency || "usd");
  }

  async subscribeTo(tier: PricingTier) {
    if (tier.comingSoon) return;

    if (tier.subscribed) {
      this.router.navigate(["/dashboard"]);
      return;
    }

    // Verify pricing is available before proceeding
    const plan = this.isAnnual() ? tier.annual?.plan : tier.monthly?.plan;
    if (!plan) {
      // Plans not loaded yet, effect will retry when they load
      return;
    }

    this.busy.set(true);
    this.error.set(undefined);

    try {
      // Clear pre-selected plan before redirect
      this.selectedPlan.set(null);

      // All tiers go through Stripe checkout with 14-day trial
      await firstValueFrom(this.subscriptionService.subscribeTo(plan));
      // Redirects to Stripe checkout
    } catch (err: any) {
      this.error.set(err.message || "Subscription failed");
      this.autoProcessing.set(false);
    } finally {
      this.busy.set(false);
    }
  }
}
